/**
 * Single app-wide icon registry.
 * Source of truth: `src/assets/icon/` (new brand SVGs).
 * Import from here anywhere (sidebar, modals, page headers) so the same
 * asset is reused — e.g. AppIcons.templates for sidebar + create/edit/delete.
 *
 * Chrome fallbacks (collapse / mobile toggle) stay from `assets/icons` here.
 * Logout uses brand pack `Logout.svg`.
 */

/* ─── Brand pack (`assets/icon`) ─── */
import aborted from "./Aborted 1.svg";
import advancedSettings from "./Advanced Settings 1.svg";
import aggregator from "./Aggregator 1.svg";
import alerts from "./Alerts 1.svg";
import batch from "./Batch 1.svg";
import blocked from "./Blocked 1.svg";
import bulkManagement from "./Bulk Management 1.svg";
import campaign from "./Campaign 1.svg";
import campaignItem from "./Campaign-1 1.svg";
import consumption from "./Consumption 1.svg";
import contacts from "./Contacts 1.svg";
import coverage from "./Coverage 1.svg";
import dashboard from "./Dashboard 1.svg";
import delivery from "./Delivery 1.svg";
import deliveryAdvance from "./Delivery Advance 1.svg";
import deliveryContent from "./Delivery Content 1.svg";
import deliveryLatency from "./Delivery Latency 1.svg";
import detailedAnalytics from "./Detailed Analytics 1.svg";
import dltCompliance from "./DLT Compliance 1.svg";
import documentation from "./Documentation 1.svg";
import sdk from "./SDK.svg";
import edr from "./EDR 1.svg";
import encodingTools from "./Encoding Tools 1.svg";
import errorCodes from "./Error Codes 1.svg";
import firewall from "./Firewall 1.svg";
import groupConnections from "./Group Connections 1.svg";
import hlrConnection from "./HLR Connection 1.svg";
import httpConnections from "./HTTP Connections 1.svg";
import lookup from "./Look-up 1.svg";
import looping from "./Looping 1.svg";
import messages from "./Messages 1.svg";
import networks from "./Networks 1.svg";
import optInPages from "./Opt-in Pages 1.svg";
import organizations from "./Organizations 1.svg";
import packetCapture from "./Packet Capture (PCAP) 1.svg";
import performance from "./Performance 1.svg";
import profit from "./Profit 1.svg";
import providers from "./Providers 1.svg";
import resellers from "./Resellers 1.svg";
import route from "./Route 1.svg";
import routing from "./Routing 1.svg";
import schedule from "./Schedule 1.svg";
import scheduleManager from "./Schedule-1 1.svg";
import senderIdManagement from "./Sender ID Management 1.svg";
import senderBasedRouting from "./Sender-Based Routing 1.svg";
import smscConnections from "./SMSC Connections 1.svg";
import summary from "./Summary 1.svg";
import templates from "./Templates 1.svg";
import tps from "./TPS 1.svg";
import trafficBlocking from "./Traffic Blocking 1.svg";
import trafficLimits from "./Traffic Limits 1.svg";
import twoWayMessaging from "./Two-Way Messaging 1.svg";
import user from "./User 1.svg";
import users from "./Users 1.svg";
import vendorSummary from "./Vendor Summary 1.svg";
import vendors from "./Vendors 1.svg";

/* ─── Navbar brand pack (`assets/icon`) ─── */
import bulkMessage from "./Bulk Message.svg";
import crdEur from "./CRD EUR.svg";
import darkLightMode from "./Dark light mode.svg";
import fileDownloadProgress from "./file download progress.svg";
import notification from "./Notification.svg";
import quickMessage from "./Quick Message.svg";
import submissionReport from "./Submission Report.svg";

/* ─── Sidebar section headers ─── */
import sectionAnalyticsReports from "./Analytics & Reports.svg";
import sectionBillingPricing from "./Billing & Pricing.svg";
import sectionComplianceSecurity from "./Compliance & Security.svg";
import sectionDeveloperTools from "./Developer Tools.svg";
import sectionEngagement from "./Engagement.svg";
import sectionMessageOperations from "./Message Operations.svg";
import sectionOrganizationAccounts from "./Organization & Accounts.svg";
import sectionVendorProviders from "./Vendor &  Providers.svg";

/* ─── User list action / modal pack ─── */
import accessToken from "./Access Token.svg";
import authentication from "./Athentication.svg";
import balance from "./balance.svg";
import blockStatus from "./Block Status.svg";
import configuration from "./Configuration.svg";
import deleteIcon from "./Delete.svg";
import filter from "./Filter.svg";
import flag from "./Flag.svg";
/* Filenames in pack are swapped vs glyph meaning — map by visual */
import hlrLookup from "./Resend DLR.svg";
import hlrSchedule from "./Schedule.svg";
import marketing from "./Marketing.svg";
import multiAccess from "./Multi access.svg";
import resend from "./Resend.svg";
import resendDlr from "./HLR lookup.svg";
import save from "./Save.svg";
import session from "./Session.svg";
import templateCompany from "./template_company 1.svg";
import transactionHistory from "./Transaction history.svg";
import transactionMode from "./Transaction mode.svg";
import utility from "./Utility.svg";
import webAccessLog from "./web acces log.svg";

/* ─── UI chrome ─── */
import logout from "./Logout.svg";
import plus from "../img/plusIcon.svg";
import collapse from "../icons/collapse.svg";
import mobToggle from "../icons/sideToggle.svg";
import submission from "../icons/submission.svg";

export const AppIcons = {
  aborted,
  advancedSettings,
  aggregator,
  alerts,
  batch,
  blocked,
  bulkManagement,
  campaign,
  campaignItem,
  consumption,
  contacts,
  coverage,
  dashboard,
  delivery,
  deliveryAdvance,
  deliveryContent,
  deliveryLatency,
  detailedAnalytics,
  dltCompliance,
  documentation,
  sdk,
  edr,
  encodingTools,
  errorCodes,
  firewall,
  groupConnections,
  hlrConnection,
  httpConnections,
  lookup,
  looping,
  messages,
  networks,
  optInPages,
  organizations,
  packetCapture,
  performance,
  profit,
  providers,
  resellers,
  route,
  routing,
  schedule,
  scheduleManager,
  senderIdManagement,
  senderBasedRouting,
  smscConnections,
  summary,
  templates,
  tps,
  trafficBlocking,
  trafficLimits,
  twoWayMessaging,
  user,
  users,
  vendorSummary,
  vendors,

  // navbar
  bulkMessage,
  crdEur,
  darkLightMode,
  fileDownloadProgress,
  notification,
  quickMessage,
  submissionReport,

  // sidebar section headers (collapsed rail + expanded)
  sectionAnalyticsReports,
  sectionBillingPricing,
  sectionComplianceSecurity,
  sectionDeveloperTools,
  sectionEngagement,
  sectionMessageOperations,
  sectionOrganizationAccounts,
  sectionVendorProviders,

  // user list actions / modals
  accessToken,
  authentication,
  balance,
  blockStatus,
  configuration,
  delete: deleteIcon,
  filter,
  flag,
  hlrLookup,
  hlrSchedule,
  marketing,
  multiAccess,
  resend,
  resendDlr,
  save,
  session,
  templateCompany,
  transactionHistory,
  transactionMode,
  utility,
  webAccessLog,

  // chrome / seller-only extras without a brand-pack twin yet
  collapse,
  logout,
  plus,
  mobToggle,
  submission,
};

export default AppIcons;
